import * as React from "react";
import { Link } from "react-router-dom";
import { AccountSummaryAlerts } from "../../AccountSummaryAlerts";
import { useLanguage } from "../../Language";
import { useGlobalConfig } from "../../GlobalConfig";
import * as dayjs from "dayjs";
import { Box, Button, Stack, Toolbar, Typography } from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import { useAccount } from "../../Account";
import { TLCEventManager } from "../../../events/EventManager";
import { PrintAccountDataEvent } from "@tlc/ls2pac/app/managers/PrintAccountDataManager";
import { LS2PAC_CONSTANTS } from "src/main/resources/js/main/constants";

export function accountSummaryLoader() {
  // TODO redirect if not a patron...
  // Do the same for the other non-patron views
  // Add a 403 Forbidden page for requests to /config pages if not a config admin
}

export const AccountSummaryPage: React.FC = () => {
  const { accountSummary, user, patron } = useAccount();

  const { translate } = useLanguage();
  const { globalSettings, publicHostInformation, adfsSettings, ldapEnabled } =
    useGlobalConfig();

  const canEditContactInfo =
    publicHostInformation?.emailEditable ||
    publicHostInformation?.addressEditable ||
    publicHostInformation?.phoneNumberEditable;
  const canEditPIN =
    publicHostInformation?.pinEditable &&
    publicHostInformation?.usePin &&
    !adfsSettings?.enabled &&
    !ldapEnabled;

  const getExpired = function (cardExpirationDate: Date) {
    const dueDate = dayjs(cardExpirationDate).endOf("day");
    const today = dayjs();

    const daysOverdue = today.diff(dueDate, "day");
    return daysOverdue > 0 ? true : false;
  };

  const isCardExpired = patron?.cardExpirationDate
    ? getExpired(new Date(patron?.cardExpirationDate))
    : false;

  return (
    <>
      <Stack direction="column" sx={{
        gap: 1
      }}>
        <Toolbar disableGutters>
          <Typography variant="h5" sx={{ flexGrow: 1 }}>
            {patron?.fullName}
          </Typography>
          <Button
            startIcon={<PrintIcon />}
            onClick={() => {
              TLCEventManager.fireEvent(PrintAccountDataEvent, {
                sections: [LS2PAC_CONSTANTS.MyAccountPrintTypes.accountSummary]
              });
            }}
          >
            {translate("print")} {translate("accountSummary")}
          </Button>
        </Toolbar>
        <AccountSummaryAlerts
          patron={patron}
          accountSummary={accountSummary}
        ></AccountSummaryAlerts>
        {globalSettings?.hidePersonalInformation === false && (
          <Box>
            <Toolbar disableGutters>
              <Typography sx={{ flexGrow: 1 }} variant="h6">
                {translate("contactInformationTab")}
              </Typography>
              {canEditContactInfo && (
                <Link to="/myaccount/edit-contact-info">
                  {translate("changeContactInformation")}
                </Link>
              )}
            </Toolbar>
            <address>
              {patron?.address1 && (
                <>
                  {patron?.address1} <br />
                </>
              )}
              {patron?.address2 && (
                <>
                  {patron?.address2} <br />
                </>
              )}
              {(patron?.city || patron?.state || patron?.zipcode) && (
                <>
                  {patron?.city}, {patron?.state} {patron?.zipcode}
                  <br />
                </>
              )}
              {patron?.phone && (
                <>
                  {patron?.phone}
                  <br />
                </>
              )}
              {patron?.altPhone && (
                <>
                  {patron?.altPhone}
                  <br />
                </>
              )}
              {patron?.smsPhone && (
                <>
                  {patron?.smsPhone}
                  <br />
                </>
              )}
              {patron?.email && (
                <>
                  {patron?.email}
                  <br />
                </>
              )}
              {patron?.email2 && (
                <>
                  {patron?.email2}
                  <br />
                </>
              )}
              {patron?.email3 && (
                <>
                  {patron?.email3}
                  <br />
                </>
              )}
            </address>
          </Box>
        )}
        <Box>
          <Toolbar disableGutters>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              {translate("cardNumber")}
            </Typography>
            {canEditPIN && (
              <Link to="/myaccount/edit-pin">
                {translate("changeYourLibraryPin")}
              </Link>
            )}
          </Toolbar>
          {user?.patronId}
          <br />
          {translate("cardExpiration")}:{" "}
          <span className={isCardExpired ? "overdue" : ""}>
            {patron?.cardExpirationDateString}
          </span>
          <br />
          {translate("accountStatus")}: {patron?.status}
        </Box>
        {user?.local === true && (
          <Box>
            <Toolbar disableGutters>
              <Typography variant="h6" sx={{ flexGrow: 1 }}>
                {translate("ezLoginTab")}
              </Typography>
              {user?.usernameSet && (
                <Box sx={{ display: "inline" }}>
                  <Link to="/myaccount/edit-username">
                    {translate("updateEZUsernameQuickLink")}
                  </Link>{" "}
                  {translate("or")}{" "}
                  <Link to="/myaccount/edit-password">
                    {translate("updateEZPasswordQuickLink")}
                  </Link>
                </Box>
              )}
            </Toolbar>
            {user?.usernameSet && user?.username}
            {user?.usernameSet === false && (
              <Link to="/myaccount/create-username">
                {translate("createEZLogin")}
              </Link>
            )}
          </Box>
        )}
      </Stack>
    </>
  );
};
